import { __ } from '@wordpress/i18n';

const formBlockContents = document.querySelectorAll('.block-form__content');
const fileRequestBlocks = Array.from(
	document.querySelectorAll('.block-form[data-file-post-id]')
);
const config = { childList: true };

const getSafeUrl = (url) => {
	try {
		const safeUrl = new URL(url, window.location.href);

		if (!['http:', 'https:'].includes(safeUrl.protocol)) {
			return '';
		}

		return safeUrl.href;
	} catch {
		return '';
	}
};

const createFileResource = (formBlock) => {
	const resource = document.createElement('div');
	const title = document.createElement('p');
	const copy = document.createElement('p');

	resource.classList.add('block-form__resource');
	resource.setAttribute('aria-live', 'polite');
	resource.setAttribute('role', 'status');
	resource.setAttribute('tabindex', '-1');
	resource.setAttribute('aria-busy', 'true');

	title.classList.add('block-form__resource-title');
	title.textContent = __('Thank you. Your file is ready.', 'propel');

	copy.classList.add('block-form__resource-copy');
	copy.textContent = __('Retrieving your file…', 'propel');

	resource.append(title, copy);

	// Placed where the form was so the panel takes its position.
	const form = formBlock.querySelector(
		'.gform_confirmation_wrapper, .hs-form-frame, .hbspt-form'
	);

	if (form) {
		form.insertAdjacentElement('beforebegin', resource);
	} else {
		formBlock.append(resource);
	}

	return { resource, title, copy };
};

const displayFileError = ({ resource, title, copy }) => {
	title.textContent = __('Thank you for your submission.', 'propel');
	copy.textContent = __(
		'We were unable to retrieve your file. Please refresh the page and try again.',
		'propel'
	);
	resource.removeAttribute('aria-busy');
};

const displayFile = (formBlock) => {
	const postId = formBlock.dataset.filePostId;

	if (!postId || formBlock.dataset.fileRequestState) {
		return;
	}

	formBlock.dataset.fileRequestState = 'loading';

	const d = new Date();
	d.setTime(d.getTime() + 365 * 24 * 60 * 60 * 1000);
	document.cookie = `${postId}-file-request=1;expires=${d.toUTCString()};path=/`;

	const fileResource = createFileResource(formBlock);
	const { resource, copy } = fileResource;

	fetch(`${window.propel.siteUrl}/wp-json/propel/v1/file-url/${postId}/`)
		.then((response) => response.json())
		.then((data) => {
			const fileUrl = getSafeUrl(data?.url ?? '');

			if (!fileUrl) {
				displayFileError(fileResource);
				return;
			}

			const link = document.createElement('a');

			link.classList.add(
				'block-form__resource-link',
				'c-btn',
				'c-btn--primary'
			);
			link.href = fileUrl;
			link.textContent = __('Access the file', 'propel');
			link.setAttribute('target', '_blank');
			link.setAttribute('rel', 'noopener noreferrer');

			copy.textContent = __(
				'Use the link below to access your copy.',
				'propel'
			);
			resource.append(link);
			resource.classList.add('block-form__resource--ready');
			resource.removeAttribute('aria-busy');

			formBlock.dataset.fileRequestState = 'unlocked';
			resource.focus();
		})
		.catch((error) => {
			// eslint-disable-next-line no-console
			console.log(error);

			displayFileError(fileResource);
		});
};

const getHubSpotFormBlock = (formId) => {
	if (!formId || 'string' !== typeof formId) {
		return null;
	}

	const normalizedFormId = formId.toLowerCase();

	const matchedBlock = fileRequestBlocks.find((formBlock) =>
		Array.from(
			formBlock.querySelectorAll('[data-form-id], form[id^="hsForm_"]')
		).some(
			(form) =>
				form.dataset.formId?.toLowerCase() === normalizedFormId ||
				form.id.toLowerCase() === `hsform_${normalizedFormId}`
		)
	);

	if (matchedBlock) {
		return matchedBlock;
	}

	// Iframed HubSpot embeds don't expose the form ID in the DOM, so fall back to the only candidate.
	const hubspotBlocks = fileRequestBlocks.filter((formBlock) =>
		formBlock.querySelector('.hbspt-form, .hs-form-frame')
	);

	return hubspotBlocks.length === 1 ? hubspotBlocks[0] : null;
};

const submitCallback = (mutationList, observer) => {
	if (mutationList.length > 0) {
		for (const mutation of mutationList) {
			if (mutation.addedNodes) {
				let confirmationWrapper;

				const hasConfirmationWrapper = Array.from(
					mutation.addedNodes
				).some((node) => {
					if (
						node.classList &&
						node.classList.contains('gform_confirmation_wrapper')
					) {
						confirmationWrapper = node;

						return true;
					}

					return false;
				});

				if (hasConfirmationWrapper) {
					const formSubmittedEvent = new CustomEvent(
						'form-submit-success',
						{
							detail: {
								formId:
									confirmationWrapper?.id.replace(
										'gform_confirmation_wrapper_',
										'gform-'
									) ?? '',
							},
						}
					);
					window.dispatchEvent(formSubmittedEvent);

					mutation.target.classList.add(
						'block-form__content--success'
					);

					const fileRequestBlock = mutation.target.closest(
						'.block-form[data-file-post-id]'
					);

					if (fileRequestBlock) {
						displayFile(fileRequestBlock);
					}

					observer.disconnect();
					break;
				}
			}
		}
	}
};

if (formBlockContents.length > 0) {
	formBlockContents.forEach((formBlockContent) => {
		const observer = new MutationObserver(submitCallback);

		observer.observe(formBlockContent, config);
	});
}

if (fileRequestBlocks.length > 0) {
	fileRequestBlocks.forEach((formBlock) => {
		formBlock.dataset.fileRequest = 'enabled';
	});

	// Legacy HubSpot embeds.
	window.addEventListener('message', (e) => {
		if (
			e.data?.type === 'hsFormCallback' &&
			e.data.eventName === 'onFormSubmitted'
		) {
			const formBlock = getHubSpotFormBlock(e.data.data?.formGuid);

			if (formBlock) {
				displayFile(formBlock);
			}
		}
	});

	// HubSpot v4 embeds.
	window.addEventListener('hs-form-event:on-submission:success', (e) => {
		const formBlock = getHubSpotFormBlock(e.detail?.formId);

		if (formBlock) {
			displayFile(formBlock);
		}
	});
}
